import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { UploadCloud, Video, Trash2, Eye, XCircle, CheckCircle, Loader2, Ban, Search, CheckSquare, Square } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL;

const VideoManager = ({ character, apiKey }) => {
    const [videos, setVideos] = useState([]);
    const [filesToUpload, setFilesToUpload] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadStatuses, setUploadStatuses] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedVideos, setSelectedVideos] = useState([]); // New state for selection
    const abortControllerRef = useRef(null);

    useEffect(() => {
        if (!character || !apiKey) {
            setVideos([]);
            return;
        }
        fetchVideos();
        setSelectedVideos([]); // Reset selection on character change
    }, [character, apiKey]);

    const filteredVideos = useMemo(() => {
        if (!searchTerm) return videos;
        const lowerCaseSearch = searchTerm.toLowerCase();
        return videos.filter(video => video.filename.toLowerCase().includes(lowerCaseSearch));
    }, [videos, searchTerm]);

    const fetchVideos = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/admin/files/list/video/${character}`, {
                headers: { 'X-API-KEY': apiKey },
            });
            setVideos(response.data || []);
        } catch (error) {
            console.error("Failed to fetch videos:", error);
        }
    };

    // --- Selection Logic ---
    const toggleSelectAll = () => {
        if (selectedVideos.length === filteredVideos.length) {
            setSelectedVideos([]);
        } else {
            setSelectedVideos(filteredVideos.map(v => v.filename));
        }
    };

    const toggleSelectVideo = (filename) => {
        setSelectedVideos(prev => 
            prev.includes(filename) 
                ? prev.filter(name => name !== filename) 
                : [...prev, filename]
        );
    };

    const handleDelete = async (filenames) => {
        const isBatch = Array.isArray(filenames);
        const count = isBatch ? filenames.length : 1;
        const message = isBatch 
            ? `Are you sure you want to delete ${count} selected videos?` 
            : `Are you sure you want to delete '${filenames}'?`;

        if (!window.confirm(message)) return;

        try {
            await axios.post(`${API_BASE_URL}/api/admin/files/delete`, 
                { character, filename: filenames, type: "video" }, // Backend now handles array or string
                { headers: { 'X-API-KEY': apiKey } }
            );
            
            if (isBatch) setSelectedVideos([]);
            await fetchVideos();
        } catch (error) {
            alert("Failed to delete video(s).");
        }
    };

    const handleUpload = async () => {
        if (filesToUpload.length === 0) return;
        setIsUploading(true);
        setUploadProgress(10);
        abortControllerRef.current = new AbortController();
        
        const initialStatuses = filesToUpload.map(file => ({
            name: file.name, status: 'pending', message: 'In queue...'
        }));
        setUploadStatuses(initialStatuses);

        for (let i = 0; i < filesToUpload.length; i++) {
            if (abortControllerRef.current.signal.aborted) break;

            const file = filesToUpload[i];
            const formData = new FormData();
            formData.append('file', file);
            formData.append('character', character);
            formData.append('type', "video");
            
            try {
                await axios.post(`${API_BASE_URL}/api/admin/files/upload`, formData, {
                    headers: { 'X-API-KEY': apiKey, 'Content-Type': 'multipart/form-data' },
                    signal: abortControllerRef.current.signal,
                });
                setUploadStatuses(prev => prev.map(s => s.name === file.name ? { ...s, status: 'success', message: 'Upload successful.' } : s));
            } catch (error) {
                if (!axios.isCancel(error)) {
                    const errorMessage = error.response?.data?.message || "Error occurred.";
                    setUploadStatuses(prev => prev.map(s => s.name === file.name ? { ...s, status: 'error', message: errorMessage } : s));
                }
            }
            setUploadProgress(((i + 1) / filesToUpload.length) * 100);
        }
        
        setFilesToUpload([]);
        document.getElementById('video-file-input').value = null;
        await fetchVideos();
        setIsUploading(false);
    };

    const handleView = async (filename) => {
        try {
            let res = await axios.get(`${API_BASE_URL}/api/files/video/${character}/${filename}`);
            window.open(res.data.url, '_blank');
        } catch (error) { alert("Video not found."); }
    };

    const StatusIcon = ({ status }) => {
        switch (status) {
            case 'pending': return <Loader2 className="h-5 w-5 text-gray-400 animate-spin" />;
            case 'success': return <CheckCircle className="h-5 w-5 text-green-500" />;
            case 'error': return <XCircle className="h-5 w-5 text-red-500" />;
            case 'cancelled': return <Ban className="h-5 w-5 text-yellow-500" />;
            default: return null;
        }
    };
    
    return (
        <div className="bg-gray-100 min-h-screen antialiased">
            <div className="container mx-auto px-4 py-8">
                <main className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-6 sm:p-8">
                
                {/* Uploader Section */}
                <section className="mb-8">
                    <h3 className="text-lg font-medium text-gray-800 mb-4">Batch Upload Videos</h3>
                    <div className="flex items-center gap-4">
                        <label className={`cursor-pointer inline-flex items-center px-4 py-2 bg-white text-gray-700 font-semibold text-sm rounded-lg border border-gray-300 shadow-sm hover:bg-gray-50 ${isUploading ? "opacity-50 cursor-not-allowed" : ""}`}>
                            <Video className="h-5 w-5 mr-2" />
                            Select Videos
                            <input id="video-file-input" type="file" multiple accept="video/*" className="hidden" onChange={(e) => setFilesToUpload(Array.from(e.target.files))} disabled={isUploading} />
                        </label>
                        <button onClick={handleUpload} disabled={isUploading || filesToUpload.length === 0} className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white font-semibold text-sm rounded-lg shadow-md hover:bg-indigo-700 disabled:opacity-50">
                            <UploadCloud className="h-5 w-5 mr-2" />
                            Upload {filesToUpload.length > 0 ? filesToUpload.length : ''} File(s)
                        </button>
                    </div>
                </section>

                {/* Progress Section */}
                {uploadProgress > 0 && (
                    <section className="p-4 border border-gray-200 rounded-lg mb-4 bg-gray-50">
                        <div className="flex justify-between items-center mb-2">
                            <p className="text-sm font-medium text-gray-700">Progress: {Math.round(uploadProgress)}%</p>
                            <button onClick={() => abortControllerRef.current?.abort()} className="text-sm text-red-600 hover:text-red-800 font-semibold">{isUploading ? "Stop" : "Close"}</button>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                            <div className="bg-indigo-600 h-2.5 rounded-full transition-all" style={{ width: `${uploadProgress}%` }}></div>
                        </div>
                    </section>
                )}

                {/* Video List Section */}
                <section>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-4">
                        <h3 className="text-lg font-medium text-gray-800">Uploaded Videos</h3>
                        
                        {selectedVideos.length > 0 && (
                            <button 
                                onClick={() => handleDelete(selectedVideos)}
                                className="inline-flex items-center px-3 py-1.5 bg-red-50 text-red-700 border border-red-200 rounded-md hover:bg-red-100 transition-colors text-sm font-bold"
                            >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete Selected ({selectedVideos.length})
                            </button>
                        )}
                    </div>

                    <div className="mb-4 relative">
                        <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                            <Search className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                            type="text"
                            placeholder="Search videos..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="block w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                    </div>

                    <div className="border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th scope="col" className="px-4 py-3 text-left">
                                        <button onClick={toggleSelectAll} className="text-gray-500 hover:text-indigo-600">
                                            {selectedVideos.length === filteredVideos.length && filteredVideos.length > 0 
                                                ? <CheckSquare className="h-5 w-5" /> 
                                                : <Square className="h-5 w-5" />
                                            }
                                        </button>
                                    </th>
                                    <th scope="col" className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Filename</th>
                                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredVideos.length > 0 ? filteredVideos.map((video, i) => (
                                    <tr key={video.filename} className={selectedVideos.includes(video.filename) ? "bg-indigo-50" : ""}>
                                        <td className="px-4 py-4 whitespace-nowrap">
                                            <button onClick={() => toggleSelectVideo(video.filename)} className="text-gray-400 hover:text-indigo-600">
                                                {selectedVideos.includes(video.filename) 
                                                    ? <CheckSquare className="h-5 w-5 text-indigo-600" /> 
                                                    : <Square className="h-5 w-5" />
                                                }
                                            </button>
                                        </td>
                                        <td className="px-2 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{video.filename}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <div className="flex items-center justify-end space-x-4">
                                                <button onClick={() => handleView(video.filename)} className="text-indigo-600 hover:text-indigo-900 inline-flex items-center"><Eye className="h-4 w-4 mr-1" /> View</button>
                                                <button onClick={() => handleDelete(video.filename)} className="text-red-600 hover:text-red-900 inline-flex items-center"><Trash2 className="h-4 w-4 mr-1" /> Delete</button>
                                            </div>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr><td colSpan="3" className="text-center py-10 text-gray-500">No videos found.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </section>
            </main>
            </div>
        </div>
    );
};

export default VideoManager;